import type {
  ShopifyProduct,
  ShopifyProductOptionKey,
  ShopifyProductVariant,
} from "../api/ShopifyApi";

import type { ProductOption } from "../api/StorefrontApi";

export class ProductUtil {
  getFormState({
    product,
    options,
    autoSelect = false,
    change,
  }: {
    product: ShopifyProduct;
    options: ProductFormOptions;
    autoSelect?: boolean;
    change?: ProductFormOptions;
  }): FormState {
    const selectedOptions = this.getSelectedOptions({
      product,
      options,
      change,
      autoSelect,
    });

    const hasSelection =
      selectedOptions &&
      Object.values(selectedOptions).every((value) => !!value);

    const optionsWithValues = this.getOptionsWithValues({
      product,
      selectedOptions,
    });

    const selectedVariant = this.getVariantByOptions({
      product,
      options: selectedOptions,
      optionsWithValues,
    });

    return {
      selectedVariant,
      selectedOptions,
      hasSelection,
      optionsWithValues,
      hasOnlyDefaultVariant:
        product.options.length === 1 &&
        product.options[0].name === "Title" &&
        product.options[0].values.length == 1,
    };
  }

  getSelectedOptions({
    product,
    options,
    autoSelect,
    change = {},
  }: {
    product: ShopifyProduct;
    options: ProductFormOptions;
    autoSelect?: boolean;
    change?: ProductFormOptions;
  }): ProductFormOptions {
    const nextOptions = Object.fromEntries(
      Object.entries({
        ...options,
        ...change,
      }).map(([optionKey, value], index) => {
        if (!value && product.options[index]?.values.length === 1) {
          // Always select single option values
          return [optionKey, product.options[index].values[0]];
        }
        return [optionKey, value];
      }),
    );

    if (!autoSelect) {
      return nextOptions;
    }

    const lastChangedKey = Object.keys(change).pop();

    const fixedOptions = !lastChangedKey
      ? Object.fromEntries(
          Object.entries(options).filter(([, value]) => !!value),
        )
      : Object.fromEntries(
          Object.entries(nextOptions).filter(([key]) => lastChangedKey > key),
        );

    const variableOptions = Object.fromEntries(
      Object.entries(nextOptions).filter(([key]) => !(key in fixedOptions)),
    );

    const autoSelectOptions = Object.keys(variableOptions).reduce(
      (options, optionKey) => {
        const variants = this.getAllVariantsByOptions({
          product,
          options,
        });

        if (!variants.length) {
          // Reset option, it has not variants
          return {
            ...options,
            [optionKey]: null,
          };
        }

        // Replace option with auto select
        return {
          ...options,
          [optionKey]: (variants.find((variant) => variant.available) ??
            variants[0])[optionKey as ShopifyProductOptionKey],
        };
      },
      fixedOptions,
    );

    return autoSelectOptions;
  }

  getVariantOptions({
    variant,
  }: {
    variant?: ShopifyProductVariant | null;
  }): ProductFormOptions {
    return {
      option1: variant?.option1 ?? null,
      option2: variant?.option2 ?? null,
      option3: variant?.option3 ?? null,
    };
  }

  getOptionsWithValues({
    product,
    selectedOptions = {},
  }: {
    product: ShopifyProduct;
    selectedOptions?: ProductFormOptions;
  }): ProductFormOption[] {
    return product.options.map((option, i) => {
      const optionKey = `option${i + 1}` as ShopifyProductOptionKey;

      const unavailableValues = this.getUnavailableOptionValues({
        product,
        optionKey,
        selectedOptions,
        values: option.values,
      });

      const soldoutValues = this.getSoldoutOptionValues({
        product,
        optionKey,
        selectedOptions,
        values: option.values,
      });

      const selectedValue = selectedOptions?.[optionKey] ?? null;

      return {
        ...option,
        selectedValue,
        soldoutValues,
        unavailableValues,
      };
    });
  }

  getSoldoutOptionValues({
    product,
    optionKey,
    values,
    selectedOptions = {},
  }: {
    product: ShopifyProduct;
    optionKey: ShopifyProductOptionKey;
    values: string[];
    selectedOptions?: ProductFormOptions;
  }) {
    // Only use previous options
    const optionsFilter = Object.fromEntries(
      Object.entries(selectedOptions).filter(([key]) => key < optionKey),
    );

    return values.filter((value) => {
      const matchingVariants = product.variants.filter((variant) =>
        this.matchesVariant({
          variant,
          options: {
            ...optionsFilter,
            [optionKey]: value,
          },
        }),
      );

      if (!matchingVariants.length) {
        return false;
      }

      return matchingVariants.every((variant) => !variant.available);
    });
  }

  getUnavailableOptionValues({
    product,
    optionKey,
    values,
    selectedOptions = {},
  }: {
    product: ShopifyProduct;
    optionKey: ShopifyProductOptionKey;
    values: string[];
    selectedOptions?: ProductFormOptions;
  }) {
    // Only use previous options
    const optionsFilter = Object.fromEntries(
      Object.entries(selectedOptions).filter(([key]) => key < optionKey),
    );

    return values.filter((value) =>
      product.variants.every(
        (variant) =>
          !this.matchesVariant({
            variant,
            options: {
              ...optionsFilter,
              [optionKey]: value,
            },
          }),
      ),
    );
  }

  getFirstAvailableVariant({ product }: { product: ShopifyProduct }) {
    return product.variants.find((variant) => variant.available) ?? null;
  }

  getSelectedVariant({
    product,
    variantId,
  }: {
    product: ShopifyProduct;
    variantId?: number;
  }) {
    return product.variants.find((variant) => variant.id === variantId) ?? null;
  }

  matchesVariant({
    variant,
    options,
  }: {
    variant: ShopifyProductVariant;
    options?: ProductFormOptions;
  }) {
    return (
      (!options?.option1 || options.option1 === variant.option1) &&
      (!options?.option2 || options.option2 === variant.option2) &&
      (!options?.option3 || options.option3 === variant.option3)
    );
  }

  getFormData({
    form,
    product,
  }: {
    form: HTMLFormElement;
    product: ShopifyProduct;
  }) {
    const formData = new FormData(form);

    return {
      quantity: this.getFormQuantity({ form }),
      options: this.getFormOptions({
        formData,
        product,
      }),
    };
  }

  getFormQuantity({ form }: { form: HTMLFormElement }) {
    const formData = new FormData(form);
    const quantity = formData.get("quantity")?.toString();

    if (!quantity) {
      return 1;
    }

    return parseInt(quantity);
  }

  getFormProperties({ form }: { form: HTMLFormElement }) {
    const formData = new FormData(form);

    return Array.from(formData.entries()).reduce(
      (props, item) => {
        if (!item[0].startsWith("properties[")) return props;

        const key = item[0].replace("properties[", "").replace("]", "");

        return {
          ...props,
          [key]: `${item[1]}`,
        };
      },
      {} as Record<string, string>,
    );
  }

  getVariantByOptions({
    product,
    options,
    optionsWithValues,
  }: {
    product: ShopifyProduct;
    options: ProductFormOptions;
    optionsWithValues: ProductFormOption[];
  }) {
    const optionList = Object.entries(options).reduce(
      (options, [, value], index) => {
        options[index] = value;
        return options;
      },
      new Array<string | null>(3).fill(null),
    );

    return (
      product.variants.find((variant) => {
        return optionList.every((value, index) => {
          if (!optionsWithValues[index]) {
            // Ignore options without values
            return true;
          }
          return (
            variant[`option${index + 1}` as ShopifyProductOptionKey] === value
          );
        });
      }) ?? null
    );
  }

  getAllVariantsByOptions({
    product,
    options,
  }: {
    product: ShopifyProduct;
    options: ProductFormOptions;
  }) {
    const optionList = Object.entries(options).reduce(
      (options, [, value], index) => {
        options[index] = value;
        return options;
      },
      new Array<string | null>(3).fill(null),
    );

    return (
      product.variants.filter((variant) => {
        return optionList.every(
          (value, index) =>
            !value ||
            variant[`option${index + 1}` as ShopifyProductOptionKey] === value,
        );
      }) ?? []
    );
  }

  getFormOptions({
    formData,
    product,
  }: {
    formData: FormData;
    product: ShopifyProduct;
  }) {
    return Array.from(formData.entries()).reduce((options, [key, data]) => {
      const name = key.match(/\[(.*?)\]/)?.[1];

      if (!name) {
        return options;
      }

      const optionIndex =
        product.options.findIndex((option) => option.name === name) + 1;

      if (optionIndex > 0) {
        const optionKey = `option${optionIndex}` as ShopifyProductOptionKey;
        options[optionKey] = data.toString() || null;
      }

      return options;
    }, {} as ProductFormOptions);
  }

  getInputOption({
    input,
    product,
  }: {
    input: HTMLInputElement;
    product: ShopifyProduct;
  }) {
    const name = input.name.match(/\[(.*?)\]/)?.[1];

    if (!name) {
      return null;
    }

    const optionIndex =
      product.options.findIndex((option) => option.name === name) + 1;

    if (optionIndex > 0) {
      const optionKey = `option${optionIndex}` as ShopifyProductOptionKey;
      return {
        [optionKey]: input.value.toString(),
      };
    }

    return null;
  }
}

export interface FormState {
  selectedVariant: ShopifyProductVariant | null;
  selectedOptions: ProductFormOptions;
  hasSelection: boolean;
  optionsWithValues: ProductFormOption[];
  hasOnlyDefaultVariant: boolean;
}

export type ProductFormOptions = Partial<
  Record<ShopifyProductOptionKey, string | null>
>;

export interface ProductFormOption extends ProductOption {
  selectedValue: string | null;
  soldoutValues: string[];
  unavailableValues: string[];
}
